import React from 'react';
import { CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const PricingPage = () => {
  // Hardcoded Buy Now URLs for static site deployment
  const packages = [
    {
      id: 1,
      name: 'EQUI RIDER',
      price: '1,500',
      priceValue: 1500,
      duration: 30,
      // Direct payment URL
      buyNowUrl: 'https://provisionpro.equipulseresearch.in/authentication/user-register?payload=eyJzZXJ2aWNlX25hbWUiOiAiRVFVSSBSSURFUiIsICJkdXJhdGlvbiI6IDMwLCAicHJpY2UiOiAxNTAwfQ%3D%3D',
      features: [
        '2–3 short-term swing trade ideas per week (Cash segment)',
        'Clear entry, stop-loss, and target levels',
        'Weekly market commentary',
        'Instant alerts via Telegram and WhatsApp',
        'Broadcast-only service (no personalized advisory)'
      ],
      benefits: [
        'Clean, no-hype trade ideas',
        'Strict risk–reward discipline',
        'Best suited for self-driven traders'
      ],
      popular: false
    },
    {
      id: 2,
      name: 'EQUI HUNTER',
      price: '6,666',
      priceValue: 6666,
      duration: 30,
      // Direct payment URL
      buyNowUrl: 'https://provisionpro.equipulseresearch.in/authentication/user-register?payload=eyJzZXJ2aWNlX25hbWUiOiAiRVFVSSBIVU5URVIiLCAiZHVyYXRpb24iOiAzMCwgInByaWNlIjogNjY2Nn0%3D',
      features: [
        '2–3 Single day trade Cash Calls daily',
        '2–3 Index/Stock Derivatives Calls daily',
        '2–3 high-conviction swing trades per month',
        'Monthly portfolio review and allocation insights',
        'Daily market brief and weekly market report',
        'Delivery via Telegram and WhatsApp'
      ],
      benefits: [
        'High-frequency trading opportunities',
        'Strategic swing ideas for capital growth',
        'Portfolio support without full-time advisory fees'
      ],
      popular: true
    },
    {
      id: 3,
      name: 'EQUI MASTER',
      price: '8,333',
      priceValue: 8333,
      duration: 30,
      // Direct payment URL
      buyNowUrl: 'https://provisionpro.equipulseresearch.in/authentication/user-register?payload=eyJzZXJ2aWNlX25hbWUiOiAiRVFVSSBNQVNURVIiLCAiZHVyYXRpb24iOiAzMCwgInByaWNlIjogODMzM30%3D',
      features: [
        '2–3 Single day trade Cash Calls daily',
        '2–3 Index/Stock Derivatives Calls daily',
        '2–3 Swing Trade Ideas monthly',
        'Optional commodity calls (Gold, Silver, Crude Oil)',
        'Long-term investment themes and earnings impact analysis',
        'Daily market brief and weekly market report',
        'Priority access to webinars and events'
      ],
      benefits: [
        'Active traders in Cash and F&O segments',
        'Serious investors and HNI clients',
        'Clients seeking tax efficiency and portfolio-level support'
      ],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Pricing Plans | Affordable Stock Advisory | Equi Pulse Research"
        description="Choose from our affordable stock advisory plans starting at ₹1,500/month. Get swing trades, intraday tips, and derivatives calls from SEBI-registered analysts."
        keywords="stock advisory pricing, trading subscription, affordable stock tips, investment advisory plans, SEBI registered"
        url="/pricing"
      />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=1920')] bg-cover bg-center opacity-10" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Stock Advisory <span className="text-amber-400">Pricing Plans</span>
            </h1>
            <p className="text-xl text-gray-300">
              Affordable subscription plans for expert stock market research and trading recommendations.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative p-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border ${
                  pkg.popular ? 'border-amber-500/50 shadow-2xl shadow-amber-900/20' : 'border-emerald-700/30'
                } hover:border-amber-500/50 transition-all duration-300`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-6 py-1.5 bg-gradient-to-r from-amber-600 to-amber-500 text-white text-sm font-bold rounded-full shadow-lg">
                    Most Popular
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-amber-400 mb-4">{pkg.name}</h3>
                  <div className="mb-2">
                    <span className="text-5xl font-bold text-white">₹ {pkg.price}</span>
                    <span className="text-gray-400 text-lg">/-*</span>
                  </div>
                  <p className="text-gray-400 text-sm">Monthly</p>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-emerald-400 mb-4">What You Get</h4>
                  <ul className="space-y-3">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-amber-400 mb-4">Why Choose {pkg.name}</h4>
                  <ul className="space-y-2">
                    {pkg.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-amber-500 mt-1">•</span>
                        <span className="text-gray-300 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-2">
                  <a
                    href={pkg.buyNowUrl}
                    data-testid={`buy-now-btn-${pkg.id}`}
                    className="block w-full px-6 py-4 bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 text-white font-semibold rounded-lg shadow-lg shadow-emerald-900/50 transition-all duration-300 text-center"
                  >
                    Subscribe to {pkg.name}
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div className="max-w-3xl mx-auto mt-12">
            <p className="text-gray-400 text-sm text-center">
              * All the above mentioned prices are for monthly subscription excluding GST
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-950 via-slate-950 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Not Sure Which Plan{' '}
              <span className="text-amber-400">Is Right for You?</span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Book a free consultation with our experts to find the perfect plan for your investment goals.
            </p>
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 text-white font-semibold rounded-lg shadow-2xl shadow-emerald-900/50 transition-all duration-300"
            >
              Book Free Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;